import { Link } from "react-router-dom";
import { X, Check, Minus, Scale } from "lucide-react";
import { useApp } from "../context/AppContext";
import { formatINR, configLabel } from "../lib/format";

const ROWS = [
  ["Price", (p) => p.listing_type === "rent" ? `${formatINR(p.price)}/mo` : formatINR(p.price)],
  ["Location", (p) => `${p.locality}, ${p.city}`],
  ["Area", (p) => `${p.area_sqft.toLocaleString("en-IN")} sq.ft`],
  ["Price / sq.ft", (p) => `₹${p.price_per_sqft.toLocaleString("en-IN")}`],
  ["Configuration", (p) => configLabel(p)],
  ["Developer", (p) => p.developer],
  ["Possession", (p) => p.possession_status],
  ["RERA", (p) => p.rera ? "Registered" : "—"],
  ["Verified", (p) => p.verified ? "yes" : "no"],
  ["Amenities", (p) => (p.amenities || []).slice(0, 6).join(", ")],
];

export default function Compare() {
  const { compare, toggleCompare, clearCompare } = useApp();

  if (compare.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <Scale size={48} className="mx-auto text-pbs-purple/40" />
        <h1 className="mt-4 text-2xl font-extrabold text-pbs-dark">Nothing to compare yet</h1>
        <p className="mt-2 text-pbs-grey">Add up to 4 properties using the Compare button on any listing.</p>
        <Link to="/properties" className="mt-6 inline-block pbs-gradient text-white font-bold px-6 py-3 rounded-xl">Browse Properties</Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-pbs-dark">Compare Properties <span className="text-pbs-grey text-lg font-semibold">({compare.length}/4)</span></h1>
        <button onClick={clearCompare} className="text-sm font-semibold text-pbs-coral">Clear all</button>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-pbs-line bg-white pbs-shadow">
        <table className="w-full min-w-[640px]" data-testid="compare-table">
          <thead>
            <tr>
              <th className="w-40 p-4 text-left align-bottom text-sm font-bold text-pbs-grey sticky left-0 bg-white">Attribute</th>
              {compare.map((p) => (
                <th key={p.slug} className="p-4 align-top min-w-[200px]">
                  <div className="relative">
                    <button onClick={() => toggleCompare(p)} className="absolute -top-1 -right-1 bg-pbs-coral text-white rounded-full w-6 h-6 flex items-center justify-center"><X size={13} /></button>
                    <Link to={`/property/${p.slug}`}><img src={p.image} alt={p.name} className="w-full h-28 object-cover rounded-lg" /></Link>
                    <Link to={`/property/${p.slug}`} className="block mt-2 font-bold text-pbs-dark text-sm text-left leading-tight hover:text-pbs-purple">{p.name}</Link>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROWS.map(([label, fn], ri) => (
              <tr key={label} className={ri % 2 ? "bg-pbs-bg" : "bg-white"}>
                <td className="p-4 text-sm font-semibold text-pbs-grey sticky left-0 bg-inherit">{label}</td>
                {compare.map((p) => {
                  const v = fn(p);
                  return (
                    <td key={p.slug} className="p-4 text-sm text-pbs-dark align-top">
                      {v === "yes" ? <Check size={18} className="text-green-600" /> : v === "no" ? <Minus size={18} className="text-pbs-grey" /> : v}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
